import { createContext, useCallback, useContext, useState } from 'react'

import { Member } from 'src/features/member/types'
import { VideoCallRoomProvider } from 'src/features/videoCall/components/VideoCallRoomProvider/VideoCallRoomProvider'
import { getFullName } from 'src/utils/getFullName'

import { useVideoCallToken } from '../hooks/useVideoCallToken'

type Context = {
  roomUid?: string
  participantUid?: string
  participantName?: string
  videoExpanded: boolean
  showMediaNotEnabledPopover: boolean
  initializePatientVideoCall: (param: {
    roomUid: string
    candidateId: string
  }) => void
  initializeMemberVideoCall: (param: {
    roomUid: string
    member: Member
  }) => void
  toggleVideoExpanded: () => void
  setShowMediaNotEnabledPopover: (newValue: boolean) => void
}

const RoomContext = createContext<Context | undefined>(undefined)

type Props = {
  children: React.ReactNode
}

export const ExplanationRoomMediaProvider: React.FC<Props> = ({ children }) => {
  const [roomUid, setRoomUid] = useState<string>()
  const [participantUid, setParticipantUid] = useState<string>()
  const [participantName, setParticipantName] = useState<string>()

  const [videoExpanded, setFloatingVideoExpanded] = useState(true)
  const [showMediaNotEnabledPopover, setShowMediaNotEnabledPopover] =
    useState(true)

  const jwtParam =
    !!roomUid && !!participantUid ? { roomUid, participantUid } : null

  const {
    token,
    videoCallUserUid,
    screenShareToken,
    screenShareUserUid,
    agoraCloudProxyMode,
  } = useVideoCallToken(jwtParam)

  const initializePatientVideoCall = useCallback(
    (param: { roomUid: string; candidateId: string }) => {
      if (param.roomUid === roomUid) return
      setRoomUid(param.roomUid)
      setParticipantUid(param.candidateId)
      setParticipantName(`候補ID:${param.candidateId}`)
    },
    [roomUid],
  )

  const initializeMemberVideoCall = useCallback(
    (param: { roomUid: string; member: Member }) => {
      if (param.roomUid === roomUid) return
      setRoomUid(param.roomUid)
      setParticipantUid(param.member.uid)
      setParticipantName(`${getFullName(param.member)}`)
    },
    [roomUid],
  )

  const toggleVideoExpanded = useCallback(() => {
    setFloatingVideoExpanded(prev => !prev)
  }, [])

  return (
    <VideoCallRoomProvider
      roomUid={roomUid ?? ''}
      token={token}
      videoCallUserUid={videoCallUserUid}
      screenShareUserUid={screenShareUserUid}
      screenShareToken={screenShareToken}
      scene="EConsentExplanation"
      agoraCloudProxyMode={agoraCloudProxyMode}
    >
      <RoomContext.Provider
        value={{
          roomUid,
          participantUid,
          participantName,
          videoExpanded,
          showMediaNotEnabledPopover,
          initializePatientVideoCall,
          initializeMemberVideoCall,
          toggleVideoExpanded,
          setShowMediaNotEnabledPopover,
        }}
      >
        {children}
      </RoomContext.Provider>
    </VideoCallRoomProvider>
  )
}

export const useRoomMediaContext = (): Context => {
  const context = useContext(RoomContext)
  if (!context) {
    throw new Error(
      'useRoomMediaContext must be used within a ExplanationRoomMediaProvider',
    )
  }
  return context
}
